// src/modules/assetManagement/assetApi.ts
// Mock API. Function shapes mirror the future REST calls, so connecting the backend only changes this file.
import { getState, setState } from "./mockDb";
import { coverAssetLinks, dropUncoveredProducts } from "./linkRules";
import type {
  Asset,
  AssetFilters,
  AttemptResolution,
  LibraryEvent,
  LibraryProduct,
  SupportTicket,
  UploadAttemptLog,
} from "./types";

const LATENCY_MS = 180;
/** Mock cold-storage restore time; real restores take hours */
export const MOCK_RESTORE_MS = 20_000;

const delay = <T>(value: T, ms = LATENCY_MS): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const nowIso = () => new Date().toISOString();
const uid = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const CURRENT_USER = (() => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return (u?.email as string) || "manager@acme-retail.com";
  } catch {
    return "manager@acme-retail.com";
  }
})();

const restoreTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Move assets whose restore time has passed back to standard storage */
const settleRestores = () => {
  const now = Date.now();
  const due = getState().assets.some(
    (a) =>
      a.storage === "restoring" &&
      a.restoreReadyAt &&
      new Date(a.restoreReadyAt).getTime() <= now,
  );
  if (!due) return;
  setState((s) => ({
    ...s,
    assets: s.assets.map((a) =>
      a.storage === "restoring" &&
      a.restoreReadyAt &&
      new Date(a.restoreReadyAt).getTime() <= now
        ? { ...a, storage: "standard", restoreReadyAt: null, updatedAt: nowIso() }
        : a,
    ),
  }));
};

const scheduleRestore = (asset: Asset) => {
  if (!asset.restoreReadyAt || restoreTimers.has(asset.id)) return;
  const wait = Math.max(0, new Date(asset.restoreReadyAt).getTime() - Date.now());
  restoreTimers.set(
    asset.id,
    setTimeout(() => {
      restoreTimers.delete(asset.id);
      settleRestores();
    }, wait + 50),
  );
};

// Resume timers for restores started before a page refresh
getState()
  .assets.filter((a) => a.storage === "restoring")
  .forEach(scheduleRestore);

const patchAssets = (ids: string[], patch: (a: Asset) => Asset) => {
  const set = new Set(ids);
  setState((s) => ({
    ...s,
    assets: s.assets.map((a) => (set.has(a.id) ? patch(a) : a)),
  }));
};

const matchesFilters = (a: Asset, f: AssetFilters) => {
  const q = f.search?.trim().toLowerCase();
  if (q) {
    const { events, products } = getState();
    const linkedNames = [
      ...events.filter((e) => a.eventIds.includes(e.id)).map((e) => e.title),
      ...products.filter((p) => a.productIds.includes(p.id)).map((p) => p.title),
    ];
    const haystack = [a.filename, ...a.tags, ...linkedNames].join(" ").toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  const any = <T,>(chosen: T[] | undefined, has: (v: T) => boolean) => !chosen?.length || chosen.some(has);
  if (!any(f.eventIds, (id) => a.eventIds.includes(id))) return false;
  if (!any(f.productIds, (id) => a.productIds.includes(id))) return false;
  if (!any(f.tags, (t) => a.tags.includes(t))) return false;
  if (!any(f.statuses, (st) => (st === "attention" ? a.status === "failed" || a.status === "rejected" : a.status === st))) return false;
  if (!any(f.storages, (st) => a.storage === st)) return false;
  return true;
};

export const assetApi = {
  // ---- reads ---------------------------------------------------------------
  async listAssets(filters: AssetFilters = {}): Promise<Asset[]> {
    settleRestores();
    const list = getState()
      .assets.filter((a) => matchesFilters(a, filters))
      .sort((x, y) => y.uploadedAt.localeCompare(x.uploadedAt));
    return delay(list);
  },

  async getAsset(id: string): Promise<Asset | null> {
    settleRestores();
    return delay(getState().assets.find((a) => a.id === id) ?? null);
  },

  async listEvents(): Promise<LibraryEvent[]> {
    // Events deleted on the Events page are soft-deleted (isActive = false)
    return delay(getState().events.filter((e) => e.isActive !== false));
  },

  async listProducts(): Promise<LibraryProduct[]> {
    return delay(getState().products);
  },

  async listTags(): Promise<string[]> {
    const tags = new Set<string>();
    getState().assets.forEach((a) => a.tags.forEach((t) => tags.add(t)));
    return delay([...tags].sort());
  },

  async listUploadLog(): Promise<UploadAttemptLog[]> {
    return delay([...getState().uploadLog].sort((a, b) => b.at.localeCompare(a.at)));
  },

  // ---- links (Library + Asset Management) -----------------------------------
  async linkAssets(assetIds: string[], eventIds: string[], productIds: string[]) {
    const events = getState().events;
    patchAssets(assetIds, (a) => {
      const nextProducts = [...new Set([...a.productIds, ...productIds])];
      return {
        ...a,
        // A product link always comes with an event that contains the product
        eventIds: coverAssetLinks(events, [...new Set([...a.eventIds, ...eventIds])], nextProducts),
        productIds: nextProducts,
        updatedAt: nowIso(),
      };
    });
    return delay(true);
  },

  async unlinkAssets(assetIds: string[], eventIds: string[], productIds: string[]) {
    const events = getState().events;
    patchAssets(assetIds, (a) => {
      const nextEvents = a.eventIds.filter((id) => !eventIds.includes(id));
      return {
        ...a,
        eventIds: nextEvents,
        // Products whose events were all unlinked go too
        productIds: dropUncoveredProducts(events, nextEvents, a.productIds.filter((id) => !productIds.includes(id))),
        updatedAt: nowIso(),
      };
    });
    return delay(true);
  },

  // ---- Asset Management only -------------------------------------------------
  async archiveAssets(ids: string[]) {
    patchAssets(ids, (a) =>
      a.storage === "standard" ? { ...a, storage: "archived", updatedAt: nowIso() } : a,
    );
    return delay(true);
  },

  async restoreAssets(ids: string[]) {
    const readyAt = new Date(Date.now() + MOCK_RESTORE_MS).toISOString();
    patchAssets(ids, (a) =>
      a.storage === "archived"
        ? { ...a, storage: "restoring", restoreReadyAt: readyAt, updatedAt: nowIso() }
        : a,
    );
    getState()
      .assets.filter((a) => ids.includes(a.id) && a.storage === "restoring")
      .forEach(scheduleRestore);
    return delay(true);
  },

  async shareAssets(assetIds: string[], email: string, expiresAt: string) {
    const share = { id: uid("shr"), assetIds, email, expiresAt, createdAt: nowIso() };
    setState((s) => ({ ...s, shares: [share, ...s.shares] }));
    return delay(share);
  },

  async deleteAssets(ids: string[]) {
    const set = new Set(ids);
    setState((s) => ({ ...s, assets: s.assets.filter((a) => !set.has(a.id)) }));
    return delay(true);
  },

  /** "Fix": replace a failed/rejected asset's file and reprocess it */
  async replaceAssetFile(id: string, file: File) {
    patchAssets([id], (a) => ({
      ...a,
      filename: file.name,
      sizeBytes: file.size,
      mimeType: file.type || a.mimeType,
      status: "queued",
      errorMessage: null,
      updatedAt: nowIso(),
    }));
    setTimeout(() => patchAssets([id], (a) => ({ ...a, status: "transcoding" })), 1500);
    setTimeout(
      () =>
        patchAssets([id], (a) => ({
          ...a,
          status: "completed",
          previewUrl:
            a.kind === "image" || a.kind === "video" ? URL.createObjectURL(file) : a.previewUrl,
        })),
      4500,
    );
    return delay(true);
  },

  async createAsset(input: Omit<Asset, "id" | "uploadedAt" | "updatedAt" | "uploadedBy">) {
    const asset: Asset = {
      ...input,
      id: uid("ast"),
      uploadedBy: CURRENT_USER,
      uploadedAt: nowIso(),
      updatedAt: nowIso(),
    };
    setState((s) => ({ ...s, assets: [asset, ...s.assets] }));
    return delay(asset, 60);
  },

  // ---- first-line support -----------------------------------------------------
  async raiseSupportTicket(input: Omit<SupportTicket, "ref" | "createdAt">) {
    const ticket: SupportTicket = {
      ...input,
      ref: `SUP-${10413 + getState().tickets.length}`,
      createdAt: nowIso(),
    };
    setState((s) => ({ ...s, tickets: [ticket, ...s.tickets] }));
    return delay(ticket, 400);
  },

  // ---- upload failure log -------------------------------------------------------
  async logUploadFailure(entry: Omit<UploadAttemptLog, "id" | "at" | "user" | "resolution">) {
    const row: UploadAttemptLog = {
      ...entry,
      id: uid("log"),
      at: nowIso(),
      user: CURRENT_USER,
      resolution: "open",
    };
    setState((s) => ({ ...s, uploadLog: [row, ...s.uploadLog] }));
    return delay(row, 0);
  },

  /** Updates every open attempt for a file (e.g. after a retry succeeds or a ticket is raised) */
  async resolveFileAttempts(fileKey: string, resolution: AttemptResolution, ticketRef?: string) {
    setState((s) => ({
      ...s,
      uploadLog: s.uploadLog.map((r) =>
        r.fileKey === fileKey && r.resolution === "open"
          ? { ...r, resolution, ticketRef: ticketRef ?? r.ticketRef }
          : r,
      ),
    }));
    return delay(true, 0);
  },

  async clearResolvedLog() {
    setState((s) => ({ ...s, uploadLog: s.uploadLog.filter((r) => r.resolution === "open") }));
    return delay(true);
  },
};